import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Heading,
  Divider,
  Icon,
} from '@chakra-ui/react';
import { Sparkles, Check, X } from 'lucide-react';

interface RockRewriteComparisonModalProps {
  isOpen: boolean;
  onClose: () => void;
  originalRock: string;
  originalMeasurable: string;
  aiRock: string;
  aiMeasurable: string;
  aiExplanation?: string;
  onApplySuggestion: () => void;
  onKeepOriginal: () => void;
}

/**
 * Rock Rewrite Comparison Modal Component
 * Displays side-by-side comparison of original rock vs AI-suggested rewrite
 * Migrated from Tailwind to Chakra UI
 */
export function RockRewriteComparisonModal({
  isOpen,
  onClose,
  originalRock,
  originalMeasurable,
  aiRock,
  aiMeasurable,
  aiExplanation,
  onApplySuggestion,
  onKeepOriginal,
}: RockRewriteComparisonModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <Icon as={Sparkles} w={6} h={6} color="purple.600" />
            <Heading size="lg">AI Rock Rewrite Suggestion</Heading>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={6} align="stretch">
            {aiExplanation && (
              <Box bg="blue.50" border="1px" borderColor="blue.200" borderRadius="lg" p={4}>
                <Heading size="sm" color="blue.900" mb={2}>AI Explanation</Heading>
                <Text color="blue.800" fontSize="sm">{aiExplanation}</Text>
              </Box>
            )}

            <HStack spacing={4} align="stretch">
              {/* Original Rock */}
              <Box flex={1} border="1px" borderColor="gray.200" borderRadius="lg" p={4}>
                <HStack spacing={2} mb={3}>
                  <Text fontWeight="bold" color="gray.700">Original Rock</Text>
                </HStack>
                <VStack spacing={3} align="stretch">
                  <Box>
                    <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
                      Rock:
                    </Text>
                    <Text color="gray.800" fontSize="sm">{originalRock}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
                      Measurable:
                    </Text>
                    <Text color="gray.800" fontSize="sm">{originalMeasurable}</Text>
                  </Box>
                </VStack>
              </Box>

              {/* AI Rewrite */}
              <Box flex={1} border="2px" borderColor="purple.300" borderRadius="lg" p={4} bg="purple.50">
                <HStack spacing={2} mb={3}>
                  <Icon as={Sparkles} w={4} h={4} color="purple.600" />
                  <Text fontWeight="bold" color="purple.700">AI Suggestion</Text>
                </HStack>
                <VStack spacing={3} align="stretch">
                  <Box>
                    <Text fontSize="xs" fontWeight="semibold" color="purple.600" mb={1}>
                      Rock:
                    </Text>
                    <Text color="purple.900" fontSize="sm">{aiRock}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" fontWeight="semibold" color="purple.600" mb={1}>
                      Measurable:
                    </Text>
                    <Text color="purple.900" fontSize="sm">{aiMeasurable}</Text>
                  </Box>
                </VStack>
              </Box>
            </HStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Divider mb={4} />
          <HStack spacing={3} w="full">
            <Button
              variant="outline"
              leftIcon={<Icon as={X} w={4} h={4} />}
              onClick={onKeepOriginal}
              flex={1}
            >
              Keep Original
            </Button>
            <Button
              colorScheme="purple"
              leftIcon={<Icon as={Check} w={4} h={4} />}
              onClick={onApplySuggestion}
              flex={1}
            >
              Apply Suggestion
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

